// Month pickers that replaced the typed YYYY-MM inputs: one button showing the choice ("Mar 2026 –
// Aug 2026") that opens a small popover with a year and its twelve months. The range picker takes
// two clicks (first month, then last) and offers the common presets. Esc or a click outside closes
// it and focus returns to the button. Month logic is ui/months.ts.
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "./icons";
import {
  compareMonths,
  currentMonth,
  lastMonths,
  longMonthLabel,
  monthLabel,
  monthOf,
  monthParts,
  monthSpan,
  MONTH_SHORT_NAMES,
  pickRangeMonth,
  rangeLabel,
  withinBounds,
  type Month,
  type MonthRange,
} from "./months";

function usePopover() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    // Focus the chosen month (or the first enabled one) when the popover opens.
    requestAnimationFrame(() => {
      const target =
        root.current?.querySelector<HTMLButtonElement>('.month-grid button[aria-pressed="true"]:not(:disabled)') ??
        root.current?.querySelector<HTMLButtonElement>(".month-grid button:not(:disabled)");
      target?.focus();
    });
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  return { open, setOpen, root, button, close };
}

function PickerButton({
  id,
  labelledBy,
  open,
  onToggle,
  buttonRef,
  children,
  describedBy,
}: {
  id: string;
  labelledBy?: string | undefined;
  open: boolean;
  onToggle(): void;
  buttonRef: { current: HTMLButtonElement | null };
  children: ComponentChildren;
  describedBy?: string | undefined;
}) {
  return (
    <button
      ref={buttonRef}
      id={id}
      type="button"
      class="picker-button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-labelledby={labelledBy ? `${labelledBy} ${id}` : undefined}
      aria-describedby={describedBy}
      onClick={onToggle}
    >
      <Icon name="calendar" />
      <span>{children}</span>
      <Icon name="chevron-down" small />
    </button>
  );
}

function YearHead({ year, setYear, minYear, maxYear }: { year: number; setYear(year: number): void; minYear?: number | undefined; maxYear?: number | undefined }) {
  return (
    <div class="month-picker-head">
      <button
        type="button"
        class="icon-button"
        aria-label={`Previous year, ${year - 1}`}
        disabled={minYear !== undefined && year <= minYear}
        onClick={() => setYear(year - 1)}
      >
        <Icon name="chevron-left" />
      </button>
      <span aria-live="polite">{year}</span>
      <button
        type="button"
        class="icon-button"
        aria-label={`Next year, ${year + 1}`}
        disabled={maxYear !== undefined && year >= maxYear}
        onClick={() => setYear(year + 1)}
      >
        <Icon name="chevron-right" />
      </button>
    </div>
  );
}

export interface MonthPickerProps {
  id: string;
  /** The id of the label (LabelText / Field label) naming this control. */
  labelledBy?: string | undefined;
  value: Month;
  onChange(month: Month): void;
  min?: Month | undefined;
  max?: Month | undefined;
  describedBy?: string | undefined;
}

/** One month. */
export function MonthPicker({ id, labelledBy, value, onChange, min, max, describedBy }: MonthPickerProps) {
  const { open, setOpen, root, button, close } = usePopover();
  const [year, setYear] = useState(() => monthParts(value).year);
  const today = currentMonth(new Date());
  useEffect(() => {
    if (open) setYear(monthParts(value).year);
  }, [open, value]);
  return (
    <div class="popover-anchor" ref={root}>
      <PickerButton
        id={id}
        labelledBy={labelledBy}
        open={open}
        onToggle={() => setOpen(!open)}
        buttonRef={button}
        describedBy={describedBy}
      >
        {monthLabel(value)}
      </PickerButton>
      {open && (
        <div class="popover" role="dialog" aria-label="Choose a month">
          <YearHead
            year={year}
            setYear={setYear}
            minYear={min ? monthParts(min).year : undefined}
            maxYear={max ? monthParts(max).year : undefined}
          />
          <div class="month-grid">
            {MONTH_SHORT_NAMES.map((name, index) => {
              const month = monthOf(year, index);
              return (
                <button
                  key={month}
                  type="button"
                  aria-label={longMonthLabel(month)}
                  aria-pressed={month === value}
                  data-today={month === today ? "true" : undefined}
                  disabled={!withinBounds(month, min, max)}
                  onClick={() => {
                    onChange(month);
                    close();
                  }}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export interface MonthRangePickerProps {
  id: string;
  labelledBy?: string | undefined;
  value: MonthRange;
  onChange(range: MonthRange): void;
  min?: Month | undefined;
  max?: Month | undefined;
  /** The longest range allowed, in months (the API refuses longer windows). */
  maxSpan?: number | undefined;
  describedBy?: string | undefined;
}

/** A range of months: one button, a popover, two clicks. */
export function MonthRangePicker({ id, labelledBy, value, onChange, min, max, maxSpan, describedBy }: MonthRangePickerProps) {
  const { open, setOpen, root, button, close } = usePopover();
  const [year, setYear] = useState(() => monthParts(value.to).year);
  const [pending, setPending] = useState<Month | null>(null);
  const today = currentMonth(new Date());
  const end = max ?? today;
  useEffect(() => {
    if (open) {
      setYear(monthParts(value.to).year);
      setPending(null);
    }
  }, [open]);

  const presets: { label: string; range: MonthRange }[] = [
    { label: "Last 3 months", range: lastMonths(3, end) },
    { label: "Last 6 months", range: lastMonths(6, end) },
    { label: "Last 12 months", range: lastMonths(12, end) },
    { label: "This year", range: { from: monthOf(monthParts(end).year, 0), to: end } },
  ].filter((preset) => (maxSpan === undefined || monthSpan(preset.range.from, preset.range.to) <= maxSpan) && withinBounds(preset.range.from, min, max));

  const shownFrom = pending ?? value.from;
  const shownTo = pending ?? value.to;

  return (
    <div class="popover-anchor" ref={root}>
      <PickerButton
        id={id}
        labelledBy={labelledBy}
        open={open}
        onToggle={() => setOpen(!open)}
        buttonRef={button}
        describedBy={describedBy}
      >
        {rangeLabel(value.from, value.to)}
      </PickerButton>
      {open && (
        <div class="popover" role="dialog" aria-label="Choose a range of months">
          <div class="popover-presets">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                class="button button-sm"
                onClick={() => {
                  onChange(preset.range);
                  close();
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <YearHead
            year={year}
            setYear={setYear}
            minYear={min ? monthParts(min).year : undefined}
            maxYear={max ? monthParts(max).year : undefined}
          />
          <div class="month-grid">
            {MONTH_SHORT_NAMES.map((name, index) => {
              const month = monthOf(year, index);
              const edge = month === shownFrom || month === shownTo;
              const inRange = compareMonths(month, shownFrom) > 0 && compareMonths(month, shownTo) < 0;
              return (
                <button
                  key={month}
                  type="button"
                  aria-label={longMonthLabel(month)}
                  aria-pressed={edge}
                  data-in-range={inRange ? "true" : undefined}
                  data-today={month === today ? "true" : undefined}
                  disabled={!withinBounds(month, min, max)}
                  onClick={() => {
                    const next = pickRangeMonth(pending, month, maxSpan);
                    setPending(next.pending);
                    if (next.range) {
                      onChange(next.range);
                      close();
                    }
                  }}
                >
                  {name}
                </button>
              );
            })}
          </div>
          <div class="popover-footer" aria-live="polite">
            {pending ? `From ${monthLabel(pending)}: now pick the last month.` : "Pick the first month, then the last."}
          </div>
        </div>
      )}
    </div>
  );
}
